/**
 * Cache helpers for All Reviews tag chips (Cash flow / Category / Applied tags).
 * Merge-only updates avoid blanking the grid when a page refresh fails or races.
 */

export type MergeUnifiedTxCategoryTagChipsOptions = {
  /**
   * When true, empty chip lists in ``slice`` may replace non-empty cache entries.
   * Pass a Set/array of tx ids to allow empty only for those ids (e.g. after delete refresh
   * or authoritative review-rows seed).
   */
  forceEmpty?: boolean | Set<number> | number[];
};

function forceEmptyAllows(forceEmpty: MergeUnifiedTxCategoryTagChipsOptions['forceEmpty'], txId: number): boolean {
  if (forceEmpty === true) return true;
  if (forceEmpty instanceof Set) return forceEmpty.has(txId);
  if (Array.isArray(forceEmpty)) return forceEmpty.includes(txId);
  return false;
}

/**
 * Merge a fetched/seeded slice into the per-tx chip cache.
 * Refuses to replace a non-empty chip list with ``[]`` unless ``forceEmpty`` allows it —
 * this stops filter-driven empty/raced tags GETs from blanking DB-backed chips.
 */
export function mergeUnifiedTxCategoryTagChipsByTxId<T>(
  prev: Record<number, T[]>,
  slice: Record<number, T[]>,
  opts?: MergeUnifiedTxCategoryTagChipsOptions
): Record<number, T[]> {
  const next: Record<number, T[]> = { ...prev };
  for (const [key, value] of Object.entries(slice || {})) {
    const txId = Number(key);
    if (!Number.isFinite(txId) || txId <= 0) continue;
    const chips = Array.isArray(value) ? value : [];
    const prevChips = prev[txId];
    if (
      chips.length === 0 &&
      Array.isArray(prevChips) &&
      prevChips.length > 0 &&
      !forceEmptyAllows(opts?.forceEmpty, txId)
    ) {
      continue;
    }
    next[txId] = chips;
  }
  return next;
}
